import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {ConfigurationService} from "../../../configuration/services/configuration.service";
import {ConfigurationFeature} from "../../../configuration/models/configuration";
import {ServicePathsSettings} from "../../../../core/configuration/service-paths-settings";
import {
  SignDocumentConfiguration,
  SignDocumentErrorResponse,
  SignDocumentLocalStartRequest,
  SignDocumentLocalStartResponse,
  SignDocumentRemoteContinueRequest,
  SignDocumentRemoteContinueResponse,
  SignDocumentRemoteStartResponse,
  SignDocumentUploadRequest
} from "../models/sign-document";
import {DocumentoAllegato} from "../../../models/documento-allegato";
import {map, tap} from "rxjs/operators";
import {OperatorFunction} from "rxjs";
import {operate} from "rxjs/internal/util/lift";
import {createOperatorSubscriber} from "rxjs/internal/operators/OperatorSubscriber";

export enum SignDocumentLocalSteps {
  CHECK,
  FETCH,
  START,
  CONTINUE,
  UPLOAD
}

export enum SignDocumentRemoteSteps {
  START,
  CONTINUE
}

export namespace SignDocumentServiceUrls {

  const SERVER_URL = (servicePath: string) => {
    return ServicePathsSettings.DOMINIO + ServicePathsSettings.RS_PATH + '/sign/documents' + servicePath;
  };

  export const GET_CONFIGURATION_PROPERTIES = () => {
    return SERVER_URL("/configurations");
  };

  export const START_REMOTE_SIGN = (documentId: string, signFormat: string , alias :string) => {
    return SERVER_URL(`/${documentId}/${signFormat}/${alias}/remote/start`);
  };

  export const CONTINUE_REMOTE_SIGN = (documentId: string, signFormat: string , alias :string) => {
    return SERVER_URL(`/${documentId}/${signFormat}/${alias}/remote/continue`);
  };

  const APP_URL = (config: SignDocumentConfiguration, servicePath: string) => {
    return config.appBaseUrl + '/sign/documents' + servicePath;
  };

  export const CHECK_LOCAL_SIGN = (config: SignDocumentConfiguration) => {
    return APP_URL(config, "/health");
  };

  export const DO_LOCAL_SIGN = (config: SignDocumentConfiguration) => {
    return APP_URL(config, "");
  };

  export const FETCH_ORIGINAL_DOCUMENT = (documentId: string) => {
    return ServicePathsSettings.DOMINIO + ServicePathsSettings.DOWNLOAD_DOCUMENTO + '/' + documentId;
  };

  export const UPLOAD_SIGNED_DOCUMENT = (documentId: string, signFormat: string) => {
    return SERVER_URL(`/${documentId}/${signFormat}/upload`);
  };
}

@Injectable({
  providedIn: 'root'
})
export class SignDocumentService {

  config: SignDocumentConfiguration;

  constructor(
    private configService: ConfigurationService,
    private http: HttpClient
  ) {
  }

  public checkConfigurationPermissions() {
    if (this.configService.isFeatureEnabled(ConfigurationFeature.SIGN_DOCUMENT)) {
      return;
    }
    throw new Error("The feature is not enabled!")
  }

  public getConfigurationProperties() {
    return this.http.get<SignDocumentConfiguration>(
      SignDocumentServiceUrls.GET_CONFIGURATION_PROPERTIES()
    ).pipe(
      tap(it => this.config = it)
    );
  }

  public isSignTypeAsLocal(signType: string) {
    return signType && signType.toLowerCase() === "locale"
  }

  public fetchOriginalDocument(document: DocumentoAllegato) {
    return this.http
      .get(
        SignDocumentServiceUrls.FETCH_ORIGINAL_DOCUMENT(document.id),
        {
          responseType: 'arraybuffer',
          observe: 'response',
          withCredentials: true,
        }
      )
      .pipe(map(it => this.arrayBufferToBase64(it.body)));
  }

  uploadSignedDocument(
    document: DocumentoAllegato,
    signFormat: string,
    request: SignDocumentUploadRequest
  ) {
    return this.http.put(
      SignDocumentServiceUrls.UPLOAD_SIGNED_DOCUMENT(document.id, signFormat),
      request
    );
  }

  public signDocumentAsLocalCheck(
    document: DocumentoAllegato,
    signFormat: string
  ) {
    return this.http.get<SignDocumentErrorResponse>(
      SignDocumentServiceUrls.CHECK_LOCAL_SIGN(this.config)
    ).pipe(
      this.watchError()
    );
  }

  public signDocumentAsLocalStart(
    document: DocumentoAllegato,
    signFormat: string,
    request: SignDocumentLocalStartRequest
  ) {
    return this.http.post<SignDocumentLocalStartResponse>(
      SignDocumentServiceUrls.DO_LOCAL_SIGN(this.config),
      request
    ).pipe(
      this.watchError()
    );
  }

  public signDocumentAsRemoteStart(
    document: DocumentoAllegato,
    signFormat: string,
    alias: string
  ) {
    return this.http.post<SignDocumentRemoteStartResponse>(
      SignDocumentServiceUrls.START_REMOTE_SIGN(document.id, signFormat,alias), null
    ).pipe(
      this.watchError()
    );
  }

  public signDocumentAsRemoteContinue(
    document: DocumentoAllegato,
    signFormat: string,
    alias: string,
    request: SignDocumentRemoteContinueRequest
  ) {
    return this.http.post<SignDocumentRemoteContinueResponse>(
      SignDocumentServiceUrls.CONTINUE_REMOTE_SIGN(document.id, signFormat,alias),
      request
    ).pipe(
      this.watchError()
    );
  }

  private watchError<T extends SignDocumentErrorResponse>(): OperatorFunction<T, T> {
    return operate((source, subscriber) => {
      source.subscribe(
        createOperatorSubscriber(subscriber, (value: T) => {
          if(value.errorMessage) {
            subscriber.error(new Error(value.errorMessage))
          }
          else {
            subscriber.next(value);
          }
        })
      );
    });
  }


  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

}
